import type { Category } from "@/app/api/auth-client";

export type CategoryTreeNode = { root: Category; children: Category[] };

/** True if value looks like internal catalog category_id (not for end-user display). */
export function isOpaqueCatalogCategoryId(id: string | undefined): boolean {
  const t = (id ?? "").trim();
  return /^cat_[a-zA-Z0-9_-]+$/.test(t);
}

/**
 * Builds "Root > ... > Leaf" label for a category_id using flat catalog list (same idea as BFF CategoryTreeMap).
 */
export function buildCategoryPathLabel(categories: Category[], categoryId: string): string | null {
  const id = categoryId.trim();
  if (!id || categories.length === 0) return null;
  const byID = new Map(categories.map((c) => [c.category_id.trim(), c]));
  const start = byID.get(id);
  if (!start) return null;
  const parts: string[] = [];
  let c: Category | undefined = start;
  const seen = new Set<string>();
  while (c && !seen.has(c.category_id)) {
    seen.add(c.category_id);
    parts.push(c.name || c.category_id);
    const pid = c.parent_id?.trim() ?? "";
    if (!pid) break;
    c = byID.get(pid);
  }
  if (parts.length === 0) return null;
  return parts.reverse().join(" > ");
}

/**
 * Sort categories for UI: alphabetical by name (pt-BR, diacritics-aware), then by `order` if names match.
 * Avoids `undefined - undefined` => NaN from sorting only by `order`, which scrambles siblings.
 */
function compareCategoriesForDisplay(a: Category, b: Category): number {
  const nameCmp = (a.name || "").localeCompare(b.name || "", "pt-BR", { sensitivity: "base" });
  if (nameCmp !== 0) return nameCmp;
  return (a.order ?? 0) - (b.order ?? 0);
}

/** Nested roots (no parent_id) and their children, sorted for display. */
export function buildCategoryTree(categories: Category[]): CategoryTreeNode[] {
  const byParent = new Map<string, Category[]>();
  for (const c of categories) {
    const pid = c.parent_id ?? "";
    if (!byParent.has(pid)) byParent.set(pid, []);
    byParent.get(pid)!.push(c);
  }
  for (const arr of byParent.values()) {
    arr.sort(compareCategoriesForDisplay);
  }
  const roots = (byParent.get("") ?? []).sort(compareCategoriesForDisplay);
  return roots.map((root) => ({
    root,
    children: byParent.get(root.category_id) ?? [],
  }));
}
